import React, { useEffect, useState } from 'react';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import { Circle } from '../Circle';
import { BasicCheckMarkIcon } from '../Icons';
import BodyText from '../BodyText';
import Touchable from '../Touchable';

interface ShapeCheckboxProps {
  checked: boolean;
  hollow?: boolean;
  checkedColor: BrandColor;
  uncheckedColor: BrandColor;
  circular?: boolean;
}

const ShapeCheckbox: React.FCWithChildren<ShapeCheckboxProps> = ({
  checked,
  hollow,
  checkedColor,
  uncheckedColor,
  circular,
}) => {
  const isHollow = !circular || hollow;
  const uncheckedCheckmarkColor = isHollow ? uncheckedColor : BrandColor.WhiteWash;
  const uncheckedBackgroundColor = isHollow ? BrandColor.Transparent : uncheckedColor;
  const backgroundColor = checked ? checkedColor : uncheckedBackgroundColor;
  const borderColor = checked ? checkedColor : uncheckedColor;
  const checkmarkColor = checked ? BrandColor.WhiteWash : uncheckedCheckmarkColor;
  if (circular) {
    return (
      <Circle
        diameter={20}
        color={backgroundColor}
        borderWidth={1}
        borderColor={borderColor}
      >
        <BasicCheckMarkIcon
          color={checkmarkColor}
          width={16}
          height={16}
        />
      </Circle>
    );
  }
  return (
    <Box
      borderRadius={4}
      borderWidth={2}
      borderColor={borderColor}
      borderStyle="solid"
      color={backgroundColor}
      alignItems="center"
      justifyContent="center"
      width={20}
      height={20}
    >
      {checked && (
        <BasicCheckMarkIcon
          color={checkmarkColor}
          width={16}
          height={16}
        />
      )}
    </Box>
  );
};

interface CommonCheckboxProps {
  label?: React.ReactNode | string;
  ariaLabel?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  uncheckedAppearance?: 'outline' | 'solid' | 'normal';
  checkedColor?: BrandColor;
  testID?: string;
  disabled?: boolean;
}

/**
 * A changeable checkbox may listen to change events, but doesn't have to
 */
interface ChangeableCheckbox extends CommonCheckboxProps {
  onChange?: (checked: boolean) => void;
  ignoreClick?: false;
}

/**
 * Using ignoreClick option on Checkbox will cause onChange to not trigger
 */
interface StaticCheckbox extends CommonCheckboxProps {
  onChange?: null;
  ignoreClick: true;
}

type CheckboxProps = ChangeableCheckbox | StaticCheckbox;

/**
 * Renders a stylized checkbox input with optional text label
 * @example
 * import Checkbox from './Checkbox';
 *
 * const MyComponent = () => {
 *   return (
 *     <Checkbox
 *        name="consent"
 *        checked={this.props.consent}
 *        onChange={this.updateConsent}
 *        label="&#128172; StyleSeat will use this number to text me reminders about bookings"
 *        aria-label="consent-to-use"
 *      />
 *   );
 * }
 *
 */
const Checkbox: React.FCWithChildren<CheckboxProps> = ({
  label,
  ariaLabel,
  checked,
  defaultChecked,
  onChange,
  uncheckedAppearance = 'outline',
  checkedColor = BrandColor.FloraDark,
  testID = 'checkbox',
  ignoreClick = false,
  disabled = false,
}) => {
  const [isChecked, setIsChecked] = useState<boolean>(defaultChecked || checked);

  useEffect(() => setIsChecked(checked), [checked]);

  const handleChange = () => {
    if (!ignoreClick) {
      const newValue = !isChecked;
      if (!disabled) {
        setIsChecked(newValue);
      }
      if (typeof onChange === 'function') {
        onChange(newValue);
      }
    }
  };

  return (
    <Inline>
      <Touchable
        accessibilityRole="checkbox"
        accessibilityLabel={ariaLabel || (typeof label === 'string' ? label : undefined)}
        accessibilityState={{
          checked: isChecked,
          disabled,
        }}
        aria-checked={isChecked}
        onClick={handleChange}
        testID={testID}
      >
        <Inline space={8} flexGrow={0} flexShrink={1}>
          <ShapeCheckbox
            checked={isChecked}
            uncheckedColor={BrandColor.Silver}
            checkedColor={checkedColor}
            hollow={(uncheckedAppearance) === 'outline'}
            circular={uncheckedAppearance !== 'normal'}
          />
          {label ? (
            <Inline flex={1}>
              <BodyText color={BrandColor.Metal}>{label}</BodyText>
            </Inline>
          ) : null }
        </Inline>
      </Touchable>
    </Inline>
  );
};

export default Checkbox;
